import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SimpleDialog } from '@/components/ui/SimpleDialog';
import type { ObjectType } from '../types';
import {
  useAdoptCommunityVersion,
  useApprovalContext,
  useSubmitApproval,
} from '../hooks/useApprovals';

interface AdoptCommunityButtonProps {
  objectType: ObjectType;
  objectId?: string;
}

/**
 * Převezme komunitní verzi jako firemní koncept (DRAFT) a nabídne její okamžité
 * odeslání ke schválení. Renderuje se jen když firma vyžaduje schvalování
 * (`/approvals/context/` → enabled).
 */
const AdoptCommunityButton = ({ objectType, objectId }: AdoptCommunityButtonProps) => {
  const { data: context } = useApprovalContext();
  const approvalRequired = !!context?.enabled;

  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [adoptedId, setAdoptedId] = useState<string | undefined>(undefined);

  const adopt = useAdoptCommunityVersion();
  const submit = useSubmitApproval();

  if (!approvalRequired || !objectId) return null;

  const handleAdopt = () => {
    adopt.mutate(
      { object_type: objectType, object_id: objectId },
      {
        onSuccess: (res: any) => {
          const id = res?.data?.id as string | undefined;
          if (id) {
            setAdoptedId(id);
            setConfirmSubmitOpen(true);
          }
        },
      }
    );
  };

  const handleSubmit = () => {
    if (!adoptedId) {
      setConfirmSubmitOpen(false);
      return;
    }
    submit.mutate(adoptedId, {
      onSuccess: () => {
        setConfirmSubmitOpen(false);
        setAdoptedId(undefined);
      },
    });
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={handleAdopt}
        disabled={adopt.isLoading}
        data-testid="adopt-community-btn"
      >
        <Download />
        Převzít do firmy
      </Button>

      <SimpleDialog
        open={confirmSubmitOpen}
        onOpenChange={setConfirmSubmitOpen}
        title="Odeslat ke schválení?"
        okText="Odeslat"
        cancelText="Později"
        confirmLoading={submit.isLoading}
        onCancel={() => {
          setConfirmSubmitOpen(false);
          setAdoptedId(undefined);
        }}
        onOk={handleSubmit}
      >
        <div className="text-sm text-neutral-700">
          Verze byla převzata jako firemní koncept. Chcete ji rovnou odeslat ke schválení?
        </div>
      </SimpleDialog>
    </>
  );
};

export default AdoptCommunityButton;
